/* Sequoia Fitness | Website: https://www.sequoia.fitness | Author: Ali Hosseini | Copyright (c) 2026 sequoia.fitness. All rights reserved. */
/* Designed and developed by Pouria Shabani | https://linkedin.com/in/pouriashabani */

import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { PLANS } from '../data/plans.js'
import { FavBadge } from './icons.jsx'

const APP = 'https://app.sequoia.fitness'
const CACHE_KEY = 'seq_plans_v1'
const TERMS = [1, 3, 12]
const TERM_LABELS = { 1: '۱ ماهه', 3: '۳ ماهه', 12: '۱۲ ماهه' }
const CLONES = 3
const nf = new Intl.NumberFormat('fa-IR')
const FAV_INDEX = Math.max(0, PLANS.findIndex((p) => p.fav))

const valid = (d) =>
  d &&
  Array.isArray(d.plans) &&
  d.plans.every(
    (p) =>
      p &&
      typeof p.key === 'string' &&
      Array.isArray(p.features) &&
      Array.isArray(p.variants) &&
      p.variants.length > 0 &&
      p.variants.every((v) => Number.isFinite(v.final) && v.final > 0 && Number.isFinite(v.actual) && Number.isFinite(v.months)),
  )

const toOverrides = (d) => {
  const out = {}
  d.plans.forEach((p) => {
    const prices = {}
    p.variants.forEach((v) => {
      if (TERMS.indexOf(v.months) > -1) prices[v.months] = p.key === 'sequoia pro' ? v.actual : v.final
    })
    out[p.key.replace(/^sequoia\s+/, '')] = {
      features: p.features.length ? p.features.map((f) => [f.enabled ? 1 : 0, String(f.text).slice(0, 200)]) : null,
      prices,
    }
  })
  return out
}

const readCache = () => {
  try {
    const x = JSON.parse(sessionStorage.getItem(CACHE_KEY) || 'null')
    if (x && Date.now() - x.t < 300000 && valid(x.d)) return x.d
  } catch (e) {
    return null
  }
  return null
}

let inflight = null
function loadPlans() {
  if (inflight) return inflight
  const cached = readCache()
  if (cached) {
    inflight = Promise.resolve(toOverrides(cached))
    return inflight
  }
  inflight = fetch(APP + '/api/order/public-plans', { method: 'GET', credentials: 'omit', mode: 'cors', cache: 'default' })
    .then((r) => (r.ok ? r.json() : null))
    .then((r) => {
      const d = r && r.data
      if (!valid(d)) return null
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ t: Date.now(), d }))
      } catch (e) {
        return toOverrides(d)
      }
      return toOverrides(d)
    })
    .catch(() => null)
  return inflight
}

function priceLabel(prices, term) {
  const v = prices && prices[String(term)]
  return v ? nf.format(v) : null
}

function priceSize(label) {
  if (label === null) return '1.2rem'
  if (label.length >= 10) return '1.2rem'
  if (label.length >= 8) return '1.45rem'
  return '1.9rem'
}

function Card({ plan, override, term, extra, clone }) {
  const prices = override && override.prices ? override.prices : plan.prices
  const features = override && override.features ? override.features : plan.features
  const label = prices ? priceLabel(prices, term) : null
  const longOnly = label === null && term === 1 && (plan.key === 'elite' || plan.key === 'apex')
  const dana = plan.badgeDana ? { fontFamily: 'dana' } : undefined
  return (
    <article
      className={`neo-card ${plan.cls}`.trim()}
      data-plan={plan.key === 'studio' ? undefined : plan.key}
      data-neo-clone={clone ? '1' : undefined}
      aria-hidden={clone ? 'true' : undefined}
      inert={clone ? true : undefined}
      {...extra}
    >
      <div className="neo-head">
        {plan.fav && (
          <span className="fav-badge">
            <FavBadge />
          </span>
        )}
        <span className={plan.fav ? 'neo-badge' : 'neo-badge'} style={dana}>
          {plan.badge}
        </span>
      </div>
      <ul className="neo-body">
        {features.filter(([ok]) => ok).map(([ok, text], i) => (
          <li className={ok ? 'ok' : 'no'} key={i}>
            <p>{text}</p>
          </li>
        ))}
      </ul>
      <div className="card-footer">
        {plan.btn.full ? (
          <a className="neo-btn neo-btn--full" href={plan.btn.href} target="_blank" rel="noopener">
            <span className="neo-btn-a">{plan.btn.parts[0]}</span>
            <span className="neo-btn-b">{plan.btn.parts[1]}</span>
          </a>
        ) : (
          <>
            <div className={'neo-price' + (label === null ? ' is-na' : '')}>
              <strong dir={longOnly ? 'rtl' : 'ltr'} className="neo-amount" style={{ fontSize: longOnly ? '1rem' : priceSize(label) }}>
                <span className="price price--month">{label === null ? (longOnly ? 'فقط ۳ و ۱۲ ماهه' : 'ناموجود') : label}</span>
              </strong>{' '}
              {!longOnly && <span className="neo-currency">تومان</span>}
            </div>
            <a className="neo-btn" href={plan.btn.href} target="_blank" rel="noopener">
              {plan.btn.text}
            </a>
          </>
        )}
      </div>
      {Array.from({ length: plan.overlays }, (_, i) => (
        <div className="neo-card-overlay" key={i} />
      ))}
    </article>
  )
}

export default function Pricing() {
  const total = PLANS.length
  const sectionRef = useRef(null)
  const wrapRef = useRef(null)
  const trackRef = useRef(null)
  const [term, setTerm] = useState(1)
  const [shown, setShown] = useState(1)
  const [overrides, setOverrides] = useState({})
  const [h, setH] = useState(CLONES + FAV_INDEX)
  const [wide, setWide] = useState(false)
  const [noTrans, setNoTrans] = useState(true)
  const [dragging, setDragging] = useState(false)
  const [tick, setTick] = useState(0)
  const cardRefs = useRef([])
  const guard = useRef(false)
  const dragRef = useRef(null)
  const centered = useRef(false)

  const slides = [...PLANS.slice(-CLONES), ...PLANS, ...PLANS.slice(0, CLONES)]
  const last = CLONES + total

  useEffect(() => {
    const box = document.getElementById('pricing-cont')
    if (!box || !window.fetch) return undefined
    let done = false
    const run = () => {
      if (done) return
      done = true
      loadPlans().then((o) => {
        if (o) setOverrides(o)
      })
    }
    if ('IntersectionObserver' in window) {
      const io = new IntersectionObserver(
        (es) => {
          if (es.some((e) => e.isIntersecting)) {
            io.disconnect()
            run()
          }
        },
        { rootMargin: '900px 0px' },
      )
      io.observe(box)
      return () => io.disconnect()
    }
    window.addEventListener('load', run)
    return () => window.removeEventListener('load', run)
  }, [])

  const readX = () => {
    const t = getComputedStyle(trackRef.current).transform
    if (!t || t === 'none') return 0
    let m = t.match(/^matrix\(([^)]+)\)$/)
    if (m) {
      const v = m[1].split(',').map((s) => parseFloat(s.trim()))
      return Number.isFinite(v[4]) ? v[4] : 0
    }
    m = t.match(/^matrix3d\(([^)]+)\)$/)
    if (m) {
      const v = m[1].split(',').map((s) => parseFloat(s.trim()))
      return Number.isFinite(v[12]) ? v[12] : 0
    }
    return 0
  }

  const place = useCallback(() => {
    const wrap = wrapRef.current
    const track = trackRef.current
    const card = cardRefs.current[h]
    const section = sectionRef.current
    if (!wrap || !track || !card || !section) return
    const wr = wrap.getBoundingClientRect()
    const cr = card.getBoundingClientRect()
    const cs = getComputedStyle(wrap)
    const pl = parseFloat(cs.paddingLeft) || 0
    const pr = parseFloat(cs.paddingRight) || 0
    const content = Math.max(0, wrap.clientWidth - pl - pr)
    const target = wr.left + pl + content / 2
    const current = cr.left + cr.width / 2
    section.style.setProperty('--neo-track-x', readX() + (target - current) + 'px')
  }, [h])

  const measureMode = useCallback(() => {
    setWide(window.innerWidth >= 1100 || (window.innerWidth >= 768 && window.matchMedia('(orientation:portrait)').matches))
  }, [])

  useLayoutEffect(() => {
    measureMode()
  }, [measureMode])

  useLayoutEffect(() => {
    if (!trackRef.current) return
    place()
    if (noTrans) {
      void trackRef.current.offsetHeight
      const id = requestAnimationFrame(() => setNoTrans(false))
      return () => cancelAnimationFrame(id)
    }
    return undefined
  }, [tick, wide, h, noTrans, place])

  const move = useCallback((to, animate) => {
    setH(to)
    setNoTrans(!animate)
    setTick((t) => t + 1)
  }, [])

  useEffect(() => {
    const again = () => {
      setNoTrans(true)
      setTick((t) => t + 1)
    }
    const t1 = setTimeout(again, 180)
    window.addEventListener('load', again)
    if (document.fonts && document.fonts.ready) document.fonts.ready.then(again)
    let timer = 0
    const onResize = () => {
      clearTimeout(timer)
      timer = setTimeout(() => {
        measureMode()
        again()
      }, 120)
    }
    window.addEventListener('resize', onResize, { passive: true })
    let io
    if ('IntersectionObserver' in window && sectionRef.current) {
      io = new IntersectionObserver(
        (es) => {
          es.forEach((e) => {
            if (e.isIntersecting) {
              io.disconnect()
              if (!guard.current && !centered.current) {
                centered.current = true
                move(CLONES + FAV_INDEX, false)
              }
            }
          })
        },
        { threshold: 0.3 },
      )
      io.observe(sectionRef.current)
    }
    return () => {
      clearTimeout(t1)
      clearTimeout(timer)
      window.removeEventListener('load', again)
      window.removeEventListener('resize', onResize)
      if (io) io.disconnect()
    }
  }, [measureMode, move])

  const onTransitionEnd = (e) => {
    if (e.target !== trackRef.current || e.propertyName !== 'transform' || guard.current) return
    if (h < CLONES) {
      guard.current = true
      setH(h + total)
      setNoTrans(true)
      setTick((t) => t + 1)
      requestAnimationFrame(() => (guard.current = false))
    } else if (h >= last) {
      guard.current = true
      setH(h - total)
      setNoTrans(true)
      setTick((t) => t + 1)
      requestAnimationFrame(() => (guard.current = false))
    }
  }

  const selectTerm = (n) => {
    if (n === term) return
    setTerm(n)
    const spans = sectionRef.current ? [...sectionRef.current.querySelectorAll('.price')] : []
    if (!spans.length || !spans[0].animate) {
      setShown(n)
      return
    }
    let swapped = false
    spans.forEach((s) => {
      const out = s.animate(
        [
          { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
          { opacity: 0, transform: 'translateY(10px)', filter: 'blur(2px)' },
        ],
        { duration: 130, easing: 'ease-in', fill: 'forwards' },
      )
      out.onfinish = () => {
        if (!swapped) {
          swapped = true
          setShown(n)
        }
        s.animate(
          [
            { opacity: 0, transform: 'translateY(-10px)', filter: 'blur(2px)' },
            { opacity: 1, transform: 'translateY(0)', filter: 'blur(0px)' },
          ],
          { duration: 200, easing: 'cubic-bezier(.2,.8,.2,1)', fill: 'forwards' },
        )
        out.cancel()
      }
    })
  }

  const interactive = (el) => el && (el.closest('a') || el.closest('button') || el.closest('input') || el.closest('label'))

  const onPointerDown = (e) => {
    if (interactive(e.target)) return
    dragRef.current = { x: e.clientX, y: e.clientY, start: readX(), axis: '', moved: false, dx: 0 }
    setDragging(true)
    wrapRef.current.setPointerCapture(e.pointerId)
  }
  const onPointerMove = (e) => {
    const s = dragRef.current
    if (!s) return
    const dx = e.clientX - s.x
    const dy = e.clientY - s.y
    if (!s.axis) {
      if (!(Math.abs(dx) > 6 || Math.abs(dy) > 6)) return
      s.axis = Math.abs(dx) >= Math.abs(dy) ? 'x' : 'y'
    }
    if (s.axis === 'y') {
      dragRef.current = null
      setDragging(false)
      return
    }
    s.moved = true
    s.dx = dx
    sectionRef.current.style.setProperty('--neo-track-x', s.start + dx + 'px')
  }
  const onPointerUp = () => {
    const s = dragRef.current
    if (!s) return
    dragRef.current = null
    setDragging(false)
    if (!s.moved || Math.abs(s.dx) < 10) {
      setTick((t) => t + 1)
      return
    }
    move(s.dx < 0 ? h - 1 : h + 1, true)
  }

  const dist = (t) => Math.abs(t - h)
  const cardVars = (t) => {
    const d = dist(t)
    let v = { scale: 0.82, op: 0.5, z: 1, blur: 2, ty: 0 }
    if (d === 1) v = { scale: 0.9, op: 0.75, z: 4, blur: 0, ty: 0 }
    if (d === 0) v = { scale: 1, op: 1, z: 6, blur: 0, ty: 0 }
    return {
      '--neo-scale': v.scale,
      '--neo-op': v.op,
      '--neo-z': v.z,
      '--neo-blur': v.blur + 'px',
      '--neo-ty': v.ty + 'px',
      '--neo-minh': '540px',
      '--neo-maxh': 'none',
      '--neo-cursor': d === 0 ? 'default' : 'pointer',
    }
  }
  const dotIndex = (((h - CLONES) % total) + total) % total

  return (
    <section
      className={'neo-pricing neo-center neo-relative' + (noTrans ? ' neo-slides-no-trans' : '')}
      dir="rtl"
      ref={sectionRef}
    >
      <div className="levelup"></div>
      <div className="neo-togglebar" dir="rtl">
        <div className="neo-seg" role="radiogroup" aria-label="مدت اشتراک">
          {TERMS.map((n) => (
            <button
              key={n}
              type="button"
              className={'neo-seg-btn' + (term === n ? ' is-on' : '')}
              role="radio"
              aria-checked={term === n ? 'true' : 'false'}
              data-term={n}
              onClick={() => selectTerm(n)}
            >
              {TERM_LABELS[n]}
            </button>
          ))}
        </div>
      </div>
      <div className="neo-wrap" ref={wrapRef} onPointerDown={onPointerDown} onPointerMove={onPointerMove} onPointerUp={onPointerUp} onPointerCancel={onPointerUp}>
        <div
          className={'neo-track' + (noTrans ? ' neo-no-trans' : '') + (dragging ? ' neo-dragging' : '')}
          ref={trackRef}
          onTransitionEnd={onTransitionEnd}
        >
          {slides.map((plan, t) => {
            const real = t >= CLONES && t < last
            return (
              <Card
                key={(real ? '' : 'c') + t}
                plan={plan}
                override={overrides[plan.key]}
                term={shown}
                clone={!real}
                extra={{
                  ref: (el) => (cardRefs.current[t] = el),
                  style: cardVars(t),
                  onClick: (e) => {
                    if (interactive(e.target) || t === h) return
                    move(t, true)
                  },
                }}
              />
            )
          })}
        </div>
      </div>
      <div className="neo-controls">
        <button type="button" className="neo-arrow neo-arrow--prev" aria-label="Previous" onClick={() => move(h - 1, true)}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50.95 80.66" aria-hidden="true" focusable="false">
            <path d="M19.43,2.26c9.79,6.6,21.88,19.8,28.77,29.62,1.84,2.42,2.75,4.99,2.75,8.21,0,2.9-.92,5.63-2.75,8.05-7.96,11.27-17.75,21.89-28.62,30.27-2.14,1.45-4.59,2.25-7.19,2.25-6.73,0-12.39-5.96-12.39-13.04,0-3.22.92-6.28,3.06-8.69,5.66-6.76,11.93-13.2,18.36-19.16-6.27-5.63-12.39-11.27-17.9-17.71-2.29-2.58-3.37-5.47-3.37-9.02C.11,5.8,5.62,0,12.5,0c2.45,0,4.9.8,6.89,2.26Z" />
          </svg>
        </button>
        <div className="neo-dots">
        {PLANS.map((p, i) => (
          <button key={p.key} type="button" className="neo-dot" aria-label={`Go to slide ${i + 1}`} aria-current={i === dotIndex ? 'true' : 'false'} onClick={() => move(CLONES + i, true)} />
        ))}
        </div>
        <button type="button" className="neo-arrow neo-arrow--next" aria-label="Next" onClick={() => move(h + 1, true)}>
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 50.95 80.66" aria-hidden="true" focusable="false">
            <path d="M31.52,78.4c-9.79-6.6-21.88-19.8-28.77-29.62-1.84-2.42-2.75-4.99-2.75-8.21,0-2.9.92-5.63,2.75-8.05C10.71,21.25,20.5,10.63,31.37,2.25c2.14-1.45,4.59-2.25,7.19-2.25,6.73,0,12.39,5.96,12.39,13.04,0,3.22-.92,6.28-3.06,8.69-5.66,6.76-11.93,13.2-18.36,19.16,6.27,5.63,12.39,11.27,17.9,17.71,2.29,2.58,3.37,5.47,3.37,9.02,0,7.24-5.51,13.04-12.39,13.04-2.45,0-4.9-.8-6.89-2.25Z" />
          </svg>
        </button>
      </div>
    </section>
  )
}
